import { lang } from 'zf-infra';
import dayjs from 'dayjs';

import type { CustomInputData } from './types';

const isValidInput = (input: string | number | undefined): boolean => !lang.isUndefined(input) && input !== '';

/**
 * 定义支持的运算符
 * @type {[*]}
 */
const operator: string[] = ['+', '-', '*', '/'];

/**
 * 定义运算优先级
 * @type {{+: number, -: number, *: number, /: number}}
 */
const level: { [operation: string]: number } = {
  '+': 1,
  '-': 2,
  '*': 3,
  '/': 4
};

/**
 * 定义运算具体的数学操作
 * @type {{+: ((p1?:*, p2?:*)), -: ((p1?:*, p2?:*)), *: ((p1?:*, p2?:*)), /: ((p1?:*, p2?:*))}}
 */
const func: { [operation: string]: (a: string, b: string) => number } = {
  '+': (first, second) => {
    if (first === '' || second === '') {
      return NaN;
    }
    return Number(first) + Number(second);
  },
  '-': (first, second) => {
    if (first === '' || second === '') {
      return NaN;
    }
    return Number(second) - Number(first);
  },
  '*': (first, second) => {
    if (first === '' || second === '') {
      return NaN;
    }
    return Number(first) * Number(second);
  },
  '/': (first, second) => {
    if (first === '' || second === '') {
      return NaN;
    }
    return Number(second) / Number(first);
  }
};

/**
 * 定义左右括号
 * @type {[*]}
 */
const parentheses = ['(', ')'];

/**
 * 定义支持的函数
 * @type {[*]}
 */
type FxNameType = 'SUM' | 'AVERAGE' | 'MINUTE' | 'MAX' | 'MIN';
const fxType: FxNameType[] = ['SUM', 'AVERAGE', 'MINUTE', 'MAX', 'MIN'];

/**
 * 定义具体函数执行的操作
 * @type {{SUM: ((p1?:*, p2:*)), AVERAGE: ((p1?:*, p2:*)), MINUTE: ((p1?:*, p2:*)), MAX: ((p1?:*, p2:*)), MIN: ((p1?:*, p2:*))}}
 */
const fxFunc: {
  [operation: string]: (keys: string[], input: CustomInputData) => number;
} = {
  /**
   * @return {number}
   */
  SUM: (keys: string[], input: CustomInputData) => {
    let isAllUndefined = true;
    keys.forEach(key => {
      isAllUndefined = isAllUndefined && lang.isUndefined(input[key]);
    });
    if (isAllUndefined) {
      return NaN;
    }
    let sum = 0;
    keys.forEach(key => {
      sum += lang.isUndefined(input[key]) ? 0 : Number(input[key]);
    });
    return sum;
  },
  /**
   * @return {number}
   */
  AVERAGE: (keys: string[], input: CustomInputData) => {
    let sum = 0;
    let count = 0;
    keys.forEach(key => {
      if (isValidInput(input[key])) {
        sum += Number(input[key]);
        count += 1;
      }
    });
    return sum / count;
  },
  /**
   * @return {number}
   */
  MINUTE: (keys: string[], input: CustomInputData) => {
    let difference = 0;
    if (keys.length > 1) {
      let end = input[keys[0]]?.toString().replace(/[.:]/, '');
      let start = input[keys[1]]?.toString().replace(/[.:]/, '');
      if (end != null && end.length >= 3 && start != null && start.length >= 3) {
        start = start.length === 3 ? `0${start}` : start;
        end = end.length === 3 ? `0${end}` : end;
        difference = dayjs(`2020-01-01 ${end}`, 'YYYY-MM-DD HHmm').diff(
          dayjs(`2020-01-01 ${start}`, 'YYYY-MM-DD HHmm'),
          'minutes'
        );
      }
    }
    return difference;
  },
  /**
   * @return {number}
   */
  MAX: (keys: string[], input: CustomInputData) => Math.max(...lang.compact(keys.map(key => Number(input[key])))) ?? 0,
  /**
   * @return {number}
   */
  MIN: (keys: string[], input: CustomInputData) => Math.min(...lang.compact(keys.map(key => Number(input[key])))) ?? 0
};

/**
 * 定义26个字母
 * @type {[*]}
 */
const alphabets = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z'
];

/**
 * 数据结构 - 栈
 */
class Stack {

  array: string[];

  constructor() {
    this.array = [];
  }

  /**
   * 入栈
   * @param item
   */
  push(item: string): void {
    this.array.push(item);
  }

  /**
   * 出栈
   * @returns {*}
   */
  pop(): string | undefined {
    return this.array.pop();
  }

  /**
   * 获取栈顶元素
   * @returns {*}
   */
  top(): string | undefined {
    return this.array[this.array.length - 1];
  }

}

/**
 * 数据结构 - 函数
 */
class Fx {

  expression: string;
  keys: string[] = [];
  name: string = '';

  constructor(expression: string) {
    this.expression = expression;
  }

  /**
   * 计算出函数表达式相关 key
   * SUM(A1:A3) => [A1,A2,A3]
   * SUM(B2,D3) => [B2,D3]
   * @private
   */
  _unwind(): void {
    this.keys = [];
    const [, arg] = this.expression.match(/^[A-Z]*\((.*)\)/) ?? [];
    if (arg?.includes(',')) {
      this.keys = arg.split(',');
      return;
    }

    const [start, end] = arg.split(':', 2);
    const [start1] = start;
    const [end1] = end;
    const start2 = Number(start.replace(start1 ?? '', ''));
    const end2 = Number(end.replace(end1 ?? '', ''));

    const idx = [alphabets.indexOf(start1), alphabets.indexOf(end1)];
    const cols = alphabets.slice(Math.min(...idx), (Math.max(...idx) ?? 0) + 1);

    const rows = start2 < end2 ? lang.range(start2, end2 + 1) : lang.range(end2, start2 + 1);
    cols.forEach(col => {
      rows.forEach(row => {
        this.keys.push(`${col}${row}`);
      });
    });
  }

  _build(): void {
    const [, name] = this.expression.match(/^([A-Z]*)\(.*\)/) ?? [];
    this.name = name ?? '';
    this._unwind();
  }

  compute(input: CustomInputData): number {
    if (this.name === '') {
      this._build();
    }
    return fxFunc[this.name](this.keys, input);
  }

}

/**
 * 求值表达式
 */
export default class Calculator {

  expression: string;
  rpn: string[] = [];
  fx: { [fx: string]: Fx } = {};

  constructor(expression: string) {
    this.expression = expression;
  }

  _build(): void {
    /**
     * 删除所有空格,并转大写
     */
    let input: string = lang.toUpper(this.expression.replace(/\s*/g, ''));

    /**
     * 抽出所有的函数
     */
    this.fx = this._extractFx(input);

    /**
     * 用 _fx[n] 替换函数
     */
    Object.entries(this.fx).forEach(([key, value]) => {
      input = input.replace(
        new RegExp(lang.escapeRegExp(value.expression), 'g'),
        key
      );
    });

    /**
     * 将表达式分块
     * @type {Array}
     */
    const parts: string[] = this._segment(input);

    /**
     * 将分块后的表达式,转化成后缀表达式
     * @type {Stack}
     */
    const stack = new Stack();
    const result: string[] = [];
    parts.forEach(item => {
      if (operator.includes(item) || parentheses.includes(item)) {
        if (item === ')') {
          // 依次出栈至左括号'('
          let poped = stack.pop();
          while (poped !== '(' && poped != null) {
            result.push(poped);
            poped = stack.pop();
          }
        } else if (level[stack.top() ?? ''] > level[item]) {
          while (level[stack.top() ?? ''] >= level[item]) {
            result.push(stack.pop() ?? '');
          }
          stack.push(item);
        } else {
          stack.push(item);
        }
      } else {
        result.push(item);
      }
    });

    // 栈中已有数据输出
    let poped: string | undefined = stack.pop();
    while (poped != null) {
      result.push(poped);
      poped = stack.pop();
    }
    this.rpn = result;
  }

  /**
   * 将表达式分割为数组
   *
   * @param expression
   * @returns {Array}
   * @private
   */
  _segment(expression: string): string[] {
    const parts: string[] = [];
    let part: string = '';

    expression.split('').forEach(char => {
      if (operator.includes(char) || parentheses.includes(char)) {
        if (part !== '') {
          parts.push(part);
          part = '';
        }
        parts.push(char);
        return;
      }
      part += char;
    });
    if (part !== '') {
      parts.push(part);
    }
    return parts;
  }

  // 取出函数
  _extractFx(expression: string): { [fx: string]: Fx } {
    const fxs: RegExpMatchArray[] = [];
    fxType.forEach(item => {
      const regex = new RegExp(`(${item}\\([\\w|\\d|:|,]*\\))`, 'g');
      const isMatched = expression.match(regex);
      if (isMatched != null) {
        fxs.push(isMatched);
      }
    });
    const functions: string[] = lang.uniq(lang.compact(lang.flatten(fxs)));
    const hash: { [fx: string]: Fx } = {};
    functions.forEach((fx, idx) => {
      hash[`_fx${idx + 1}`] = new Fx(fx);
    });
    return hash;
  }

  _isKey(input: number | string): boolean {
    if (lang.isNumber(input)) {
      return false;
    }

    return input.match(/^[A-Z]/) != null;
  }

  _isFx(input: string): boolean {
    return lang.startsWith(input, '_fx');
  }

  /**
   * 将后缀表达式求值
   * @param input
   */
  compute(input: CustomInputData): number | null {
    if (lang.isEmpty(this.rpn)) {
      this._build();
    }

    const stack = new Stack();
    this.rpn.forEach(item => {
      if (operator.includes(item)) {
        let first: string = stack.pop() ?? '';
        if (this._isFx(first)) {
          first = `${this.fx[first].compute(input)}`;
        } else if (this._isKey(first)) {
          first = input[first] as string ?? '';
        }

        let second: string = stack.pop() ?? '';
        if (this._isFx(second)) {
          second = `${this.fx[second].compute(input)}`;
        } else if (this._isKey(second)) {
          second = input[second] as string ?? '';
        }
        stack.push(`${func[item](first, second)}`);
      } else {
        stack.push(this._isFx(item) ? `${this.fx[item].compute(input)}` : item);
      }
    });
    const popItem = stack.pop();
    const result = popItem != null ? lang.round(Number(popItem), 2) : null;
    return lang.isNaN(result) ? null : result;
  }

}

// const c = new Calculator('10-SUM(a1:a4)*(6/3)');
// const c = new Calculator('SUM(a1:a4)');
// console.log(c.compute({
//   A1: '1',
//   A2: '2',
//   A3: '3',
//   A4: '4'
// }));
